import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { useUserContext } from './UserContext';
import {
  getFitbitDataToSynchronize,
  getLastSync
} from '../tools/WearableHelper';
import { GoogleFitHealth } from '../tools/google-fit';
import { HealthKitSync } from '../tools/health-kit';
import { useHttpFetchApi } from '../hooks/http/useHttpFetch';
import { differenceInSeconds } from 'date-fns';
import { UserAttributeSource } from '../models/UserAttribute';
import { log } from '../tools/LogHelper';
import {
  withSyncSpan,
  withSyncChildSpan,
  setSyncAttribute,
  captureSyncException,
  isSyncExceptionCaptured
} from '../common/sentry';
import { syncOuraData } from '../tools/OuraHelper';
import { GoogleHealthConnect } from '../tools/google-health-connect';
import {
  checkPhysicalActivityPermission,
  showPhysicalActivityAlert
} from '../tools/google-fit.native';
import {
  displayedWearableConnection,
  getPhoneHealthOwner,
  isPhoneHealthSource,
  phoneHealthCountsFor
} from '../tools/WearableConnection';

export enum DeviceSyncStatus {
  Started = 'Started',
  Succeeded = 'Succeeded',
  Failed = 'Failed'
}

export interface DeviceSyncInfo {
  status: DeviceSyncStatus;
  deviceName?: string;
  deviceKey: UserAttributeSource | string;
  startedAt?: Date;
  succeededAt?: Date;
  message?: string;
  sent?: number;
  total?: number;
}

export type DeviceSyncContextState = {
  deviceSyncInfo?: DeviceSyncInfo;
  clearDeviceSyncInfo: () => void;
  clearDeviceSyncState: (source: UserAttributeSource) => void;
  performBackgroundSync: () => void;
  synchronizeData: (type: UserAttributeSource) => void;
  setGoogleFitPermissionRequestInprogress: (ongoing: boolean) => void;
  lastSyncedState: {
    healthKit?: Date | null;
    googleFit?: Date | null;
    fitbit?: Date | null;
    healthConnect?: Date | null;
    oura?: Date | null;
  };
  googleFitState: 'disconnected' | 'connected';
};

export const DeviceSyncContext = createContext<DeviceSyncContextState>(null!);

export const useDeviceSyncContext = () => {
  const context = useContext(DeviceSyncContext);
  if (!context) {
    throw new Error(
      'useDeviceSyncContext must be used within the context of the DeviceSyncProvider component'
    );
  }
  return context;
};

interface DeviceSyncProviderProps {
  disableBackgroundSync?: boolean;
}

export const DeviceSyncProvider: React.FC<
  PropsWithChildren & DeviceSyncProviderProps
> = ({ children, disableBackgroundSync }) => {
  // adding to stop multiple syncs from happening at once
  const isSyncingRef = useRef(false);
  const { user } = useUserContext();
  const { error, postJson } = useHttpFetchApi();
  const [deviceSyncInfo, setDeviceSyncInfo] = useState<DeviceSyncInfo>();
  const [syncState, setSyncState] = useState<{
    healthKit?: Date | null;
    googleFit?: Date | null;
    healthConnect?: Date | null;
    fitbit?: Date | null;
  }>({});
  const synchedInitialRef = useRef(false);
  const googleFitPermissionRequestInProgress = useRef(false);
  const [googleFitState, setGoogleFitState] = useState<
    'disconnected' | 'connected'
  >('disconnected');

  useEffect(() => {
    if (error) {
      setDeviceSyncInfo(prevInfo => {
        if (!prevInfo) return prevInfo;
        return {
          ...prevInfo,
          status: DeviceSyncStatus.Failed,
          message: error?.message || 'Something went wrong, try again.'
        };
      });
    }
  }, [error]);

  const clearDeviceSyncState = (source: UserAttributeSource) => {
    setDeviceSyncInfo(undefined);
    setSyncState(syncState => ({
      ...syncState,
      [source === UserAttributeSource.HealthKit
        ? 'healthKit'
        : source === UserAttributeSource.GoogleFit
          ? 'googleFit'
          : source === UserAttributeSource.HealthConnect
            ? 'healthConnect'
            : 'fitbit']: null
    }));
  };

  const clearDeviceSyncInfo = useCallback(() => {
    setDeviceSyncInfo(undefined);
  }, []);

  const setGoogleFitPermissionRequestInprogress = useCallback(
    (ongoing: boolean) => {
      googleFitPermissionRequestInProgress.current = ongoing;
    },
    []
  );

  const synchronizeData = useCallback(
    async (type: UserAttributeSource) => {
      isSyncingRef.current = true;
      try {
        if (user?.id) {
          const connection = displayedWearableConnection(user);
          let storeUserId = connection?.userId ?? user.id;
          if (isPhoneHealthSource(type)) {
            const ownerId = await getPhoneHealthOwner(type);
            if (
              !connection ||
              !phoneHealthCountsFor(ownerId, connection.userId, user.id)
            ) {
              return;
            }
            storeUserId = ownerId ?? user.id;
          }
          const healthKit = new HealthKitSync(storeUserId);
          const googleFit = new GoogleFitHealth(storeUserId);
          const healthConnect = new GoogleHealthConnect(storeUserId);
          const fitbitAccessToken = connection?.fitbitAccessToken;
          const ouraAccessToken = connection?.ouraAccessToken;
          let grantedPermissions = [];
          if (healthConnect && type === UserAttributeSource.HealthConnect) {
            const initialized = await healthConnect.authorize();
            if (initialized) {
              grantedPermissions = await healthConnect.getGrantedPermissions();
            }
          }

          let authorized =
            healthKit && type === UserAttributeSource.HealthKit
              ? await healthKit.getAuthStatus()
              : googleFit && type === UserAttributeSource.GoogleFit
                ? await googleFit.getIsGoogleFitAuthorized()
                : healthConnect && type === UserAttributeSource.HealthConnect
                  ? grantedPermissions.length > 0
                  : undefined;
          if (googleFit && type === UserAttributeSource.GoogleFit && authorized) {
            setGoogleFitState('connected');
            const permission = await checkPhysicalActivityPermission();
            if (permission === 'never_ask_again') {
              const result = await showPhysicalActivityAlert();
              if (result === 'disconnect') {
                setGoogleFitState('disconnected');
              }
              authorized = false;
            }
          }

          if (
            (type === UserAttributeSource.Fitbit && fitbitAccessToken) ||
            (type === UserAttributeSource.Oura && ouraAccessToken) ||
            authorized
          ) {
            try {
              await withSyncSpan(
                `wearable.sync ${type}`,
                { source: type },
                async () => {
                  // if the status changes to 'Started', update the timestamp
                  setDeviceSyncInfo({
                    deviceKey: type,
                    status: DeviceSyncStatus.Started,
                    startedAt: new Date(),
                    message: 'Authorizing...'
                  });
                  const startDate = new Date();
                  setDeviceSyncInfo({
                    deviceKey: type,
                    status: DeviceSyncStatus.Started,
                    startedAt: new Date(),
                    message: 'Starting Sync...'
                  });
                  log('starting');
                  // get data to synchronize
                  const callback = (message: string) => {
                    setDeviceSyncInfo({
                      deviceKey: type,
                      status: DeviceSyncStatus.Started,
                      message
                    });
                  };
                  if (ouraAccessToken && type === UserAttributeSource.Oura) {
                    const syncedOuraDataCount = await syncOuraData(
                      connection?.userId ?? user.id
                    );
                    setSyncAttribute('records.total', syncedOuraDataCount);
                    if (syncedOuraDataCount > 0) {
                      setDeviceSyncInfo(prevInfo => ({
                        ...(prevInfo || {}),
                        deviceKey: type,
                        status: DeviceSyncStatus.Started,
                        message: `Synced ${syncedOuraDataCount} record(s)`,
                        total: syncedOuraDataCount
                      }));
                    } else {
                      setDeviceSyncInfo({
                        deviceKey: type,
                        status: DeviceSyncStatus.Started,
                        message: `No new data to sync`
                      });
                    }
                    await new Promise(res => setTimeout(res, 1000));
                  } else {
                    const userAttributes = await withSyncChildSpan(
                      `fetch ${type}`,
                      'wearable.sync.fetch',
                      { source: type },
                      async () =>
                        fitbitAccessToken && type === UserAttributeSource.Fitbit
                          ? await getFitbitDataToSynchronize(user.id, storeUserId)
                          : googleFit && type === UserAttributeSource.GoogleFit
                            ? await googleFit.getDataToSynchronize(callback)
                            : healthKit && type === UserAttributeSource.HealthKit
                              ? await healthKit.getDataToSynchronize(callback)
                              : healthConnect &&
                                  type === UserAttributeSource.HealthConnect
                                ? await healthConnect.getDataToSynchronize(
                                    callback
                                  )
                                : []
                    );
                    setSyncAttribute('records.total', userAttributes.length);
                    if (userAttributes.length > 0) {
                      setDeviceSyncInfo({
                        deviceKey: type,
                        status: DeviceSyncStatus.Started,
                        message: `Synching ${userAttributes.length}`
                      });
                      // send data in batches of 500 to server
                      await withSyncChildSpan(
                        `upload ${type}`,
                        'wearable.sync.upload',
                        {
                          source: type,
                          'records.total': userAttributes.length,
                          batches: Math.ceil(userAttributes.length / 500)
                        },
                        async () => {
                          for (let i = 0; i < userAttributes.length; i += 500) {
                            await postJson(
                              `users/${storeUserId}/attributes`,
                              userAttributes.slice(i, i + 500)
                            );
                            setDeviceSyncInfo(prevInfo => ({
                              ...(prevInfo || {}),
                              deviceKey: type,
                              status: DeviceSyncStatus.Started,
                              total: userAttributes.length,
                              message: '',
                              sent: Math.min(i + 500, userAttributes.length)
                            }));
                            // Add delay to throttle API call
                            await new Promise(res => setTimeout(res, 5000));
                          }
                        }
                      );
                    } else {
                      setDeviceSyncInfo({
                        deviceKey: type,
                        status: DeviceSyncStatus.Started,
                        message: `No new data to sync`
                      });
                      // add delay in order for user to have ample time to see the message
                      await new Promise(res => setTimeout(res, 1000));
                    }
                  }

                  const difference = differenceInSeconds(new Date(), startDate);
                  log(`awaiting ${type}.synchronizeData complete...`);
                  // if the synchronization completes successfully, update the status and timestamp
                  setDeviceSyncInfo(prevInfo => ({
                    ...(prevInfo || {}),
                    deviceKey: type,
                    message: '',
                    status: DeviceSyncStatus.Succeeded,
                    succeededAt: new Date()
                  }));
                  log(`succeeded ${type} sync in ${difference} seconds...`);
                  // set last synced date
                  const lastUserAttribute = await getLastSync(type, storeUserId);
                  setSyncState(syncState => ({
                    ...syncState,
                    [type === UserAttributeSource.HealthKit
                      ? 'healthKit'
                      : type === UserAttributeSource.GoogleFit
                        ? 'googleFit'
                        : type === UserAttributeSource.HealthConnect
                          ? 'healthConnect'
                          : type === UserAttributeSource.Fitbit
                            ? 'fitbit'
                            : 'oura']: lastUserAttribute
                      ? lastUserAttribute.created
                      : null
                  }));
                }
              );
            } catch (e) {
              log(`${type} synchronization error:`, String(e));
              if (!isSyncExceptionCaptured(e)) {
                captureSyncException(e, { source: type });
              }
              const error = e as any;
              setDeviceSyncInfo(prevInfo => ({
                ...(prevInfo || {}),
                deviceKey: type,
                status: DeviceSyncStatus.Failed,
                message: error?.message || error
              }));
            }
          }
        }
      } finally {
        isSyncingRef.current = false;
      }
    },
    [postJson, user]
  );

  const performBackgroundSync = useCallback(async () => {
    if (
      isSyncingRef.current ||
      disableBackgroundSync ||
      googleFitPermissionRequestInProgress.current
    ) {
      return;
    }
    if (user?.id) {
      isSyncingRef.current = true;
      if (Platform.OS === 'android') {
        await synchronizeData(UserAttributeSource.GoogleFit);
        await synchronizeData(UserAttributeSource.HealthConnect);
      } else {
        if (Platform.OS === 'ios') {
          await synchronizeData(UserAttributeSource.HealthKit);
        }
      }
      await synchronizeData(UserAttributeSource.Fitbit);
      await synchronizeData(UserAttributeSource.Oura);
      isSyncingRef.current = false;
    }
  }, [disableBackgroundSync, synchronizeData, user?.id]);

  // start background sync on load/login
  useEffect(() => {
    if (user?.id && !synchedInitialRef.current) {
      clearDeviceSyncInfo();
      performBackgroundSync();
      synchedInitialRef.current = true;
    }
  }, [clearDeviceSyncInfo, performBackgroundSync, user?.id]);

  const appState = useRef(AppState.currentState);

  // Add check when entering foreground
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const handleAppStateChange = async (nextAppState: AppStateStatus) => {
      log('Device Sync handle app state change');
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active' &&
        user?.id
      ) {
        performBackgroundSync();
      }
      appState.current = nextAppState;
    };
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, [performBackgroundSync, user?.id]);

  useEffect(() => {
    // Set synchedInitialRef to false when user is logged out
    // to initiate initial background sync upon login
    if (user?.id) return;
    synchedInitialRef.current = false;
  }, [user?.id]);

  return (
    <DeviceSyncContext.Provider
      value={{
        deviceSyncInfo,
        clearDeviceSyncInfo,
        clearDeviceSyncState,
        performBackgroundSync,
        synchronizeData,
        googleFitState,
        lastSyncedState: syncState,
        setGoogleFitPermissionRequestInprogress
      }}
    >
      {children}
    </DeviceSyncContext.Provider>
  );
};
